import { Badge, Figure, Nav, Navbar, NavDropdown } from 'react-bootstrap';
import { RiArrowDownSFill } from 'react-icons/ri';
import { FaRegBell, FaRegComment } from 'react-icons/fa6';
import { Link, useLocation } from 'react-router-dom';
import trustedHerdLogo from '@assets/trusted-herd-logo.svg';
import { useUserContext } from '@state/userContext';
import { BASE_PATH } from '@utils/Environment';
import { useWorkerProfileContext } from '@worker/state/workerProfileContext';
import { useWorkerInboxIndicators } from '@worker/hooks/useWorkerInbox';
import { useWorkerTodos } from '@worker/hooks/useWorkerTodos';
import { useWorkerOtherNotifications } from '@worker/hooks/useWorkerOtherNotifications';
import './styles.css';

const SERVER_URL = import.meta.env.VITE_SERVER_BASE_URL;
const DASHBOARD_PATH = `${BASE_PATH}dashboard`;

export default function THNavbar() {
  const { name, profileImageUrl } = useUserContext();
  const { surveysEnabled } = useWorkerProfileContext();
  const location = useLocation();

  const showDashboardIcons = location.pathname === DASHBOARD_PATH;

  const { data: indicators } = useWorkerInboxIndicators();
  const { data: todosData } = useWorkerTodos({
    showErrorToast: false,
    enabled: showDashboardIcons,
  });
  const { data: otherNotifsData } = useWorkerOtherNotifications(
    { offset: 0, max: 1 },
    { showErrorToast: false, enabled: showDashboardIcons }
  );

  const bellCount = (todosData?.count ?? 0) + (otherNotifsData?.countNew ?? 0);
  const bubbleCount =
    (indicators?.messageCount ?? 0) + (indicators?.directMessagesCount ?? 0);
  const formatCount = (n: number) => (n > 99 ? '99+' : String(n));

  const dashboardIcons = showDashboardIcons ? (
    <>
      <Nav.Link
        as={Link}
        to={`${BASE_PATH}inbox/todo`}
        className="link px-2 position-relative d-flex align-items-center"
      >
        <FaRegBell
          size={22}
          color={bellCount > 0 ? 'var(--yellow)' : 'white'}
        />
        {bellCount > 0 && (
          <Badge
            bg="transparent"
            pill
            className="position-absolute"
            style={{ top: '4px', right: '-8px', fontSize: '0.65rem' }}
          >
            {formatCount(bellCount)}
          </Badge>
        )}
      </Nav.Link>
      <Nav.Link
        as={Link}
        to={`${BASE_PATH}inbox/job-messages`}
        className="link px-2 position-relative d-flex align-items-center"
      >
        <FaRegComment
          size={22}
          color={bubbleCount > 0 ? 'var(--yellow)' : 'white'}
        />
        {bubbleCount >= 0 && (
          <Badge
            bg="transparent"
            pill
            className="position-absolute"
            style={{ top: '4px', right: '-8px', fontSize: '0.65rem' }}
          >
            {formatCount(bubbleCount)}
          </Badge>
        )}
      </Nav.Link>
    </>
  ) : null;

  return (
    <Navbar
      bg="dark"
      variant="dark"
      className="header"
      collapseOnSelect
      expand="lg"
    >
      <Navbar.Brand href={SERVER_URL}>
        <img src={trustedHerdLogo} alt="Trusted Herd Logo" />
        <strong>TRUSTED HERD</strong>
      </Navbar.Brand>

      {dashboardIcons && (
        <Nav className="d-flex d-lg-none ms-auto flex-row me-2 align-items-center">
          {dashboardIcons}
        </Nav>
      )}

      <Navbar.Toggle aria-controls="responsive-navbar-nav" />
      <Navbar.Collapse id="responsive-navbar-nav">
        <Nav className="ms-3">
          <Nav.Link
            as={Link}
            eventKey={DASHBOARD_PATH}
            to={DASHBOARD_PATH}
            className={`link ${
              location.pathname === BASE_PATH ? 'active' : ''
            }`}
          >
            Dash
          </Nav.Link>

          <Nav.Link
            eventKey={`${SERVER_URL}/publicJobs/index`}
            href={`${SERVER_URL}/publicJobs/index`}
            className={`link ${
              location.pathname.includes(`${BASE_PATH}jobs/`) ? 'active' : ''
            }`}
          >
            Jobs
          </Nav.Link>

          <Nav.Link
            eventKey={`${SERVER_URL}/searchCompanies/index`}
            href={`${SERVER_URL}/searchCompanies/index`}
            className="link"
          >
            Search Companies
          </Nav.Link>
          <Nav.Item className="link p-0">
            <NavDropdown
              as="div"
              title={
                <>
                  <span style={{ color: 'white', fontSize: '1.2rem' }}>
                    My Calendar
                  </span>
                  <RiArrowDownSFill className="ms-1" size={20} />
                </>
              }
            >
              <NavDropdown.Item href={`${SERVER_URL}/workerJobs/upcomingJobs`}>
                Upcoming Hired
              </NavDropdown.Item>
              <NavDropdown.Item href={`${SERVER_URL}/workerJobs/jobOffers`}>
                Offers
              </NavDropdown.Item>
              <NavDropdown.Item href={`${SERVER_URL}/workerJobs/invitations`}>
                Invitations
              </NavDropdown.Item>
              <NavDropdown.Item href={`${SERVER_URL}/workerJobs/jobHistory`}>
                Job History
              </NavDropdown.Item>
            </NavDropdown>
          </Nav.Item>
          <Nav.Link
            as={Link}
            eventKey="inbox"
            to={`${BASE_PATH}inbox/todo`}
            className={`link ${
              location.pathname.includes(`${BASE_PATH}inbox`) ? 'active' : ''
            }`}
          >
            Inbox
          </Nav.Link>
          {surveysEnabled && (
            <Nav.Link
              as={Link}
              eventKey="surveys"
              to={`${BASE_PATH}survey`}
              className={`link ${
                location.pathname === `${BASE_PATH}survey` ? 'active' : ''
              }`}
            >
              Surveys
            </Nav.Link>
          )}
          <Nav.Link
            as={Link}
            eventKey={`${BASE_PATH}workerProfile/view`}
            to={`${BASE_PATH}workerProfile/view`}
            className={`link ${
              location.pathname === `${BASE_PATH}workerProfile/view`
                ? 'active'
                : ''
            }`}
          >
            My Profile
          </Nav.Link>
        </Nav>

        <Nav className="ms-auto align-items-center">
          {dashboardIcons && (
            <div className="d-none d-lg-flex align-items-center me-3">
              {dashboardIcons}
            </div>
          )}
          <Nav.Item className="link p-0">
            <NavDropdown
              className="user-dropdown"
              title={
                <>
                  <span style={{ color: 'white', fontSize: '1.2rem' }}>
                    {name}
                  </span>
                  <Figure.Image
                    className="avatar rounded-circle"
                    src={profileImageUrl}
                  />
                  <RiArrowDownSFill className="ms-1" size={20} />
                </>
              }
            >
              <NavDropdown.Item href={`${SERVER_URL}/workerAccount`}>
                Account Settings
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to={`${BASE_PATH}workerProfile/view`}>
                My Profile
              </NavDropdown.Item>
              <NavDropdown.Item href={`${SERVER_URL}/helpUsImprove/index`}>
                Help Us Improve
              </NavDropdown.Item>
              <NavDropdown.Item href={`${SERVER_URL}/api/auth/logout`}>
                Logout
              </NavDropdown.Item>
            </NavDropdown>
          </Nav.Item>
        </Nav>
      </Navbar.Collapse>
    </Navbar>
  );
}
